import { Search } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { EmptyState, Input, cn } from '@orgus/ui';

export interface CheckOption {
  value: string;
  label: string;
  detail?: string;
  leading?: ReactNode;
}

/** A searchable list of checkboxes, for picking people or projects. Changes apply on save. */
export function CheckList({
  label,
  options,
  value,
  onChange,
  emptyText,
  maxHeight = 'max-h-64',
}: {
  label: string;
  options: CheckOption[];
  value: string[];
  onChange: (v: string[]) => void;
  emptyText: string;
  maxHeight?: string;
}) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? options.filter((o) => `${o.label} ${o.detail ?? ''} ${o.value}`.toLowerCase().includes(t)) : options;
  }, [options, q]);
  const toggle = (v: string, on: boolean) => onChange(on ? [...value, v] : value.filter((x) => x !== v));
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1.5 text-sm font-medium text-fg-muted">
        {label} <span className="font-normal text-fg-subtle num">({value.length} chosen)</span>
      </legend>
      {options.length > 6 && <Input size="sm" icon={<Search />} aria-label={`Search ${label.toLowerCase()}`} placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />}
      {options.length === 0 ? (
        <EmptyState title={emptyText} />
      ) : (
        <ul className={cn('flex flex-col overflow-y-auto rounded-md border border-border', maxHeight)}>
          {shown.map((o) => {
            const on = value.includes(o.value);
            return (
              <li key={o.value} className="border-b border-border-subtle last:border-b-0">
                <label className={cn('flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm hover:bg-hover', on ? 'text-fg' : 'text-fg-muted')}>
                  <input type="checkbox" className="size-4 shrink-0 accent-accent" checked={on} onChange={(e) => toggle(o.value, e.target.checked)} />
                  {o.leading}
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {o.detail && <span className="shrink-0 truncate text-xs text-fg-subtle">{o.detail}</span>}
                </label>
              </li>
            );
          })}
          {shown.length === 0 && <li className="px-3 py-2 text-sm text-fg-subtle">Nothing matches {q}.</li>}
        </ul>
      )}
    </fieldset>
  );
}
